import React, { useEffect, useState } from "react";
import api from "../lib/apiClient";
import { STATUS_STYLES, CHART_COLORS } from "../lib/statusConfig";
import { StatusBadge } from "../components/StatusBadge";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, LineChart, Line, Legend,
} from "recharts";
import { FileText, FolderOpen, CheckCircle2, XCircle } from "lucide-react";
import { useTheme } from "../context/ThemeContext";

const DIVISION_COLORS = { "Ramp": "#0EA5E9", "Operations/Cargo": "#F97316" };

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="border border-border bg-card p-5 rounded-sm">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <div className="font-head text-4xl font-extrabold tracking-tight tabular-nums">{value.toLocaleString()}</div>
    </div>
  );
}

function Panel({ title, children, span = "", subtitle }) {
  return (
    <div className={`border border-border bg-card rounded-sm ${span}`}>
      <div className="px-5 py-3.5 border-b border-border flex items-baseline justify-between">
        <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">{title}</h2>
        {subtitle && <span className="text-xs text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

const makeTooltip = (data, key = "value", noun = "cases") => ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = (data || []).reduce((s, d) => s + (Number(d[key]) || 0), 0);
  const val = Number(payload[0].value) || 0;
  const pct = total ? ((val / total) * 100).toFixed(1) : "0.0";
  const name = label ?? payload[0].name ?? payload[0].payload?.name;
  return (
    <div className="bg-card border border-border rounded-sm px-3 py-2 text-xs shadow-sm">
      <div className="font-medium mb-0.5">{name}</div>
      <div className="tabular-nums">{val.toLocaleString()} {noun} · {pct}%</div>
    </div>
  );
};

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();
  const { theme } = useTheme();
  const dark = theme === "dark";
  const barInk = dark ? "#E4E4E7" : "#18181B";
  const gridStroke = dark ? "#27272A" : "#E4E4E7";
  const cursorFill = dark ? "#27272A" : "#F4F4F5";

  useEffect(() => {
    api.get("/stats", { params: { division: "all" } }).then((r) => setStats(r.data));
    api.get("/grievances", { params: { limit: 6, division: "all" } }).then((r) => setRecent(r.data.slice(0, 6)));
  }, []);

  if (!stats) return <div className="p-8 text-muted-foreground text-sm">Loading dashboard…</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
      <div className="mb-8">
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Dashboard · Whole Tracker</p>
        <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Grievance Overview</h1>
        <p className="text-sm text-muted-foreground mt-1.5">Combined statistics across all divisions.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Kpi label="Total Cases" value={stats.total} icon={FileText} accent="#18181B" />
        <Kpi label="Open" value={stats.open} icon={FolderOpen} accent="#0EA5E9" />
        <Kpi label="Settled" value={stats.settled} icon={CheckCircle2} accent="#22C55E" />
        <Kpi label="Withdrawn" value={stats.withdrawn} icon={XCircle} accent="#6B7280" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="By Division">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={stats.by_division} dataKey="value" nameKey="name" innerRadius={50} outerRadius={88}
                paddingAngle={2} strokeWidth={0}>
                {stats.by_division.map((e, i) => (
                  <Cell key={e.name} fill={DIVISION_COLORS[e.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={makeTooltip(stats.by_division)} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="By Board" span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart layout="vertical" data={stats.by_bucket} margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridStroke} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
              <Tooltip content={makeTooltip(stats.by_bucket)} cursor={{ fill: cursorFill }} />
              <Bar dataKey="value" fill={barInk} radius={[0, 2, 2, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Cases by Status" span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={stats.by_status} margin={{ top: 10, right: 10, left: -6, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
              <XAxis dataKey="name" angle={-22} textAnchor="end" height={60} interval={0}
                tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: gridStroke }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={makeTooltip(stats.by_status)} cursor={{ fill: cursorFill }} />
              <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                {stats.by_status.map((e, i) => (
                  <Cell key={e.name} fill={STATUS_STYLES[e.name]?.dot || barInk} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="By Discipline">
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={stats.by_discipline} dataKey="value" nameKey="name" innerRadius={55} outerRadius={92}
                paddingAngle={2} strokeWidth={0}>
                {stats.by_discipline.map((e, i) => (
                  <Cell key={e.name} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={makeTooltip(stats.by_discipline)} />
              <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Filing Trend" span="lg:col-span-2" subtitle="last 12 months">
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={stats.trend} margin={{ top: 10, right: 20, left: -6, bottom: 10 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={{ stroke: gridStroke }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip content={makeTooltip(stats.trend, "count")} />
              <Line type="monotone" dataKey="count" stroke={barInk} strokeWidth={2}
                dot={{ r: 3, fill: barInk }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Cases by Rep">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart layout="vertical" data={stats.by_rep} margin={{ top: 0, right: 16, left: 10, bottom: 0 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
              <Tooltip content={makeTooltip(stats.by_rep)} cursor={{ fill: cursorFill }} />
              <Bar dataKey="value" fill={barInk} radius={[0, 2, 2, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel title="Top Subjects" span="lg:col-span-2">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart layout="vertical" data={stats.top_subjects} margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke={gridStroke} />
              <XAxis type="number" tick={{ fontSize: 10, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11, fill: "#52525B" }} axisLine={false} tickLine={false} />
              <Tooltip content={makeTooltip(stats.top_subjects)} cursor={{ fill: cursorFill }} />
              <Bar dataKey="value" fill="#0EA5E9" radius={[0, 2, 2, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Recent Cases">
          <div className="space-y-2.5">
            {recent.map((g) => (
              <button key={g.id} onClick={() => navigate(`/grievances/${g.id}`)}
                data-testid={`recent-case-${g.case_no}`}
                className="w-full text-left flex items-center justify-between gap-2 py-2 border-b border-border last:border-0 hover:bg-secondary/50 transition-colors rounded-sm px-1">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{g.subject}</div>
                  <div className="text-xs text-muted-foreground truncate">{g.grievant_name} · {g.discipline}</div>
                </div>
                <StatusBadge status={g.status} />
              </button>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
